/* Deals — registre transactionnel universel.
 * Le Deal est l'objet maître : les mandats Courtage s'y projettent
 * automatiquement, les autres classes d'actifs se créent directement.
 * Chaque deal porte son Score de réalisabilité (noyau + lentille actif).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import DealScorePanel from "@/components/DealScorePanel";
import OntologyExplorerButton from "@/components/OntologyExplorerButton";
import {
  Handshake, Plus, Search, Briefcase, TrendingUp, TrendingDown, Minus,
  X, Trash2, ArrowUpRight, Loader2, Layers, Wallet, Gauge, CheckCircle2,
} from "lucide-react";

const fmtCHF = (v) =>
  new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(v || 0);

function scoreColor(s) {
  if (s >= 70) return "#1D9E75";
  if (s >= 45) return "#EF9F27";
  return "#E24B4A";
}

function TrendIcon({ trend }) {
  if (trend === "up") return <TrendingUp className="w-3.5 h-3.5 text-[#1D9E75]" />;
  if (trend === "down") return <TrendingDown className="w-3.5 h-3.5 text-[#E24B4A]" />;
  return <Minus className="w-3.5 h-3.5 text-zinc-600" />;
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="noam-card p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 text-[#378ADD]" /> {label}
      </div>
      <div className="noam-display text-xl font-bold mt-1">{value}</div>
    </div>
  );
}

function NewDealModal({ meta, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", asset_class: "immobilier", valeur_chf: "", counterparty: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) { notify.error("Le titre est requis."); return; }
    setBusy(true);
    try {
      const r = await api.post("/workspace/deals", { ...form, valeur_chf: parseFloat(form.valeur_chf) || 0 });
      notify.success("Deal créé dans le registre.");
      onCreated(r.data);
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="deals-new-modal">
      <div className="noam-card p-5 w-full max-w-lg space-y-3 bg-[#0D131B]">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium flex items-center gap-2"><Handshake className="w-4 h-4 text-[#EFD127]" /> Nouveau deal</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="deals-new-close"><X className="w-4 h-4" /></button>
        </div>
        <input className="noam-input w-full text-sm" placeholder="Titre du deal (ex : Collection privée XXe, PME horlogère…)"
          value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="deals-new-title" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] text-zinc-500 mb-1">Classe d'actif</div>
            <select className="noam-input w-full text-sm" value={form.asset_class}
              onChange={(e) => setForm({ ...form, asset_class: e.target.value })} data-testid="deals-new-class">
              {Object.entries(meta.asset_classes || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] text-zinc-500 mb-1">Valeur (CHF)</div>
            <input type="number" className="noam-input w-full text-sm" placeholder="0"
              value={form.valeur_chf} onChange={(e) => setForm({ ...form, valeur_chf: e.target.value })} data-testid="deals-new-valeur" />
          </div>
        </div>
        <input className="noam-input w-full text-sm" placeholder="Contrepartie / vendeur"
          value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} data-testid="deals-new-counterparty" />
        <textarea className="noam-input w-full text-sm min-h-[90px]" placeholder="Notes — tout ce que Noam doit savoir pour juger ce deal (prix demandé, contexte, pièces disponibles…)"
          value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="deals-new-notes" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-xs">Annuler</button>
          <button onClick={submit} disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid="deals-new-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Créer le deal
          </button>
        </div>
      </div>
    </div>
  );
}

function DealDetail({ deal, statuts, onReload, onClose }) {
  const isCourtage = deal.source?.type === "courtage";
  const [notes, setNotes] = useState(deal.notes || "");
  const patch = async (fields) => {
    try {
      await api.patch(`/workspace/deals/${deal.id}`, fields);
      onReload();
    } catch (e) { notify.error(fmtError(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Supprimer ce deal du registre ?")) return;
    try {
      await api.delete(`/workspace/deals/${deal.id}`);
      notify.success("Deal supprimé.");
      onClose(); onReload();
    } catch (e) { notify.error(fmtError(e)); }
  };
  return (
    <div className="noam-card p-5 space-y-4" data-testid="deal-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="noam-display text-lg font-semibold">{deal.title}</h2>
            <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#378ADD]/40 text-[#378ADD] bg-[#378ADD]/10">{deal.asset_label}</span>
            {isCourtage ? (
              <Link to="/app/courtage" className="text-[10px] px-2 py-0.5 rounded-full border border-[#EFD127]/40 text-[#EFD127] bg-[#EFD127]/10 flex items-center gap-1 hover:bg-[#EFD127]/20"
                data-testid="deal-detail-source-courtage">
                <Briefcase className="w-3 h-3" /> Mandat Courtage <ArrowUpRight className="w-3 h-3" />
              </Link>
            ) : (
              <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-zinc-400">Création directe</span>
            )}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {fmtCHF(deal.valeur_chf)} · {deal.counterparty || "contrepartie non renseignée"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <OntologyExplorerButton otype="deal" oid={deal.id} label={deal.title} rootLabel="Deals" />
          {!isCourtage && (
            <select className="noam-input text-xs py-1 px-2 w-auto" value={deal.statut}
              onChange={(e) => patch({ statut: e.target.value })} data-testid="deal-detail-statut">
              {statuts.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {isCourtage && <span className="text-xs text-zinc-500 px-2 py-1 rounded bg-white/5">{deal.statut}</span>}
          {!isCourtage && (
            <button onClick={remove} className="btn-ghost text-xs text-[#E24B4A]" data-testid="deal-detail-delete">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={onClose} className="btn-ghost text-xs" data-testid="deal-detail-close"><X className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {!isCourtage && (
        <div>
          <div className="text-[10px] text-zinc-500 mb-1">Notes du deal (matière pour le comité)</div>
          <textarea className="noam-input w-full text-sm min-h-[80px]" value={notes}
            onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== deal.notes && patch({ notes })}
            data-testid="deal-detail-notes" />
        </div>
      )}

      <DealScorePanel endpoint={`/workspace/deals/${deal.id}`} />
    </div>
  );
}

export default function Deals() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/workspace/deals");
      setData(r.data);
      if (selected) {
        const fresh = (r.data.deals || []).find((d) => d.id === selected.id);
        setSelected(fresh || null);
      }
    } catch (e) { notify.error(fmtError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const deals = useMemo(() => {
    let list = data?.deals || [];
    if (classFilter) list = list.filter((d) => d.asset_class === classFilter);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((d) => `${d.title} ${d.counterparty}`.toLowerCase().includes(s));
    }
    return list;
  }, [data, q, classFilter]);

  if (!data) {
    return <div className="flex items-center justify-center py-20 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  const { stats, asset_classes: assetClasses, statuts } = data;

  return (
    <div className="space-y-5" data-testid="deals-root">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="noam-overline">Registre transactionnel</div>
          <h1 className="noam-display text-2xl font-bold tracking-tight">Deals</h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
            Toutes vos transactions privées, toutes classes d'actifs confondues. Les mandats Courtage s'y projettent automatiquement — les autres actifs se créent directement.
          </p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary text-xs" data-testid="deals-new-btn">
          <Plus className="w-3.5 h-3.5" /> Nouveau deal
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Layers} label="Deals actifs" value={stats.count} testid="deals-stat-count" />
        <StatCard icon={Wallet} label="Volume sous gestion" value={fmtCHF(stats.volume_chf)} testid="deals-stat-volume" />
        <StatCard icon={Gauge} label="Score moyen" value={stats.avg_score != null ? `${stats.avg_score}/100` : "—"} testid="deals-stat-avg" />
        <StatCard icon={CheckCircle2} label="Prêts au matchmaking" value={stats.ready_count} testid="deals-stat-ready" />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input className="noam-input text-xs pl-8 py-1.5 w-56" placeholder="Rechercher un deal…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="deals-search" />
        </div>
        <select className="noam-input text-xs py-1.5 px-2 w-auto" value={classFilter}
          onChange={(e) => setClassFilter(e.target.value)} data-testid="deals-filter-class">
          <option value="">Toutes classes d'actifs</option>
          {Object.entries(assetClasses).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>

      {selected && (
        <div className="flex justify-end -mb-3">
          <Link to={`/app/deals/${selected.id}`} className="btn-primary text-xs" data-testid="deal-open-360">
            <ArrowUpRight className="w-3.5 h-3.5" /> Ouvrir la fiche Deal 360
          </Link>
        </div>
      )}
      {selected && (
        <DealDetail deal={selected} statuts={statuts} onReload={load} onClose={() => setSelected(null)} />
      )}

      <div className="noam-card divide-y divide-white/5">
        {deals.length === 0 && (
          <div className="p-10 text-center text-sm text-zinc-500" data-testid="deals-empty">
            Aucun deal dans le registre. Créez-en un ou ouvrez un mandat dans le Courtage.
          </div>
        )}
        {deals.map((d) => {
          const ds = d.deal_score;
          const done = ds?.status === "done";
          return (
            <button key={d.id} onClick={() => setSelected(d)}
              className={`w-full text-left p-3.5 flex items-center gap-4 hover:bg-white/[0.03] transition-colors ${selected?.id === d.id ? "bg-white/[0.04]" : ""}`}
              data-testid={`deals-row-${d.id}`}>
              <div className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center"
                style={done
                  ? { background: `conic-gradient(${scoreColor(ds.global)} ${ds.global * 3.6}deg, rgba(255,255,255,0.06) 0deg)` }
                  : { background: "rgba(255,255,255,0.04)" }}>
                <div className="w-9 h-9 rounded-full bg-[#0D131B] flex items-center justify-center text-xs font-bold font-mono"
                  style={{ color: done ? scoreColor(ds.global) : "#52525b" }}>
                  {done ? ds.global : ds?.status === "running" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "—"}
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-zinc-100 truncate flex items-center gap-2">
                  {d.title}
                  {done && <TrendIcon trend={d.trend} />}
                </div>
                <div className="text-[11px] text-zinc-500 flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-[#378ADD]">{d.asset_label}</span>
                  <span>·</span><span>{fmtCHF(d.valeur_chf)}</span>
                  <span>·</span><span>{d.statut}</span>
                  {d.source?.type === "courtage" && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-[#EFD127]/30 text-[#EFD127]/80 flex items-center gap-1">
                      <Briefcase className="w-2.5 h-2.5" /> Courtage
                    </span>
                  )}
                </div>
              </div>
              {done && (
                <span className={`hidden sm:flex text-[10px] px-2 py-1 rounded-full border items-center gap-1 shrink-0 ${ds.matchmaking_ready ? "border-[#1D9E75]/40 text-[#1D9E75] bg-[#1D9E75]/10" : "border-[#EF9F27]/40 text-[#EF9F27] bg-[#EF9F27]/10"}`}>
                  <Handshake className="w-3 h-3" /> {ds.matchmaking_ready ? "Prêt" : "À repositionner"}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {showNew && (
        <NewDealModal meta={data} onClose={() => setShowNew(false)}
          onCreated={(deal) => { setShowNew(false); load(); setSelected(deal); }} />
      )}
    </div>
  );
}
